import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../../models/common/api.common.model';
import { GetAllMessages, HomeMessage } from '../../models/home/message.model';
import { CommonUtils } from '../../utils/common/common.utils';
import { ErrorhandleService } from '../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class HomeMessageService {
  private readonly http = inject(HttpClient);
  private readonly commonUtils = inject(CommonUtils);
  private readonly errorHandler = inject(ErrorhandleService);

  baseUrl = environment.apiUrl.concat('home-message/');

  getCurrentMessages(): Observable<GetAllMessages> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetAllMessages>(this.baseUrl.concat('get-current-messages'))
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllMessages>(
            error,
            error.status,
            'User Service: Get All Users',
            BANNER_TYPE.TABLE_DATA_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  addNewMessage(newMessage: HomeMessage): Observable<ApiResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<ApiResponse>(this.baseUrl.concat('add-message'), {
        message: newMessage,
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ApiResponse>(
            error,
            error.status,
            'Home Message Service: New Message',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
        filter((value) => !!value),
      );
  }

  editMessage(editedMessage: HomeMessage): Observable<ApiResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<ApiResponse>(this.baseUrl.concat('edit-message'), {
        message: editedMessage,
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ApiResponse>(
            error,
            error.status,
            'Home Message Service: Edit Message',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
        filter((value) => !!value),
      );
  }

  deleteMessage(messageId: number): Observable<ApiResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .delete<ApiResponse>(this.baseUrl.concat('delete-message'), {
        params: { id: messageId },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ApiResponse>(
            error,
            error.status,
            'Home Message Service: Delete Message',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
